
import { defineComponent, computed, reactive, ref, getCurrentInstance, onMounted } from 'vue';
import { message } from 'ant-design-vue';
import moment, { Moment } from 'moment';
import { noEmptyProp, not, formatWithIntl } from '@/utils/illuminate';

import { useRoute, useRouter } from 'vue-router';
import { useStore } from 'vuex';
import { createApiUrl } from '@/utils/utils';
import { request } from '@/utils/request';
import type { TableColumn } from '@/components/Table';
import { formatDate } from '@/core/filters';
import { notifyExportTaskCreated } from '@/utils/exportTaskFeedback';

import { invoiceTypeMap, invoiceStatusMap } from '@/views/fapiao/parts/utils';
import RelOrderModel from './parts/RelOrderModel.vue';
import DetailDrawer from './parts/HoupiaoDetailDrawer.vue';
import RedFlushApplyModal from './parts/RedFlushApplyModal.vue';
import RedFlushDetailModal from './parts/RedFlushDetailModal.vue';
import routeTab from './parts/routeTab.vue';
import chargeList from './parts/chargeList.vue';

const sendStatusMap: Record<string, string> = {
  0: '未邮寄',
  1: '已邮寄',
  2: '已自取',
};

export default defineComponent({
  name: 'FapiaoListView',
  components: {
    RelOrderModel,
    DetailDrawer,
    RedFlushApplyModal,
    RedFlushDetailModal,
    routeTab,
    chargeList,
  },
  setup(props, ctx) {
    const instance = getCurrentInstance()?.proxy;
    const store = useStore();
    const route = useRoute();
    const isBefore = computed(() => /xianpiao\.list$/.test(route.name as string));
    const currentErrorMsg = ref('');
    const chargeOrderRef = ref();
    const errorModalVisible = computed({
      get() {
        return !!currentErrorMsg.value;
      },
      set(val) {
        currentErrorMsg.value = val ? currentErrorMsg.value : '';
      },
    });
    const sourceList = ref([
      { value: '全部', code: '' },
    ]);

    const showError = (error: string) => {
      currentErrorMsg.value = error;
    };

    const filterParams = reactive<{
      timeRange: Moment[];
      invoiceType: string;
      invoiceStatus: string;
      enterpriseId: number | string;
      invoiceApplyType: number | string; 
    }>({
      timeRange: [],
      invoiceType: '',
      invoiceStatus: '',
      enterpriseId: '',
      invoiceApplyType: '',
    });

    const tableColumns: TableColumn[] = [
      { title: '发票抬头', dataIndex: 'invoiceTitle', align: 'center', width: '150px' },
      { title: '申请日期', dataIndex: 'createTime', customRender: ({ text }) => formatDate(text), align: 'center', width: '150px' },
      { title: '发票编号', dataIndex: 'invoiceNo', customRender: ({ text }) => text || '---', align: 'center', width: '150px' },
      { title: '申请人手机号', dataIndex: 'phone', customRender: ({ text }) => text || '---', align: 'center', width: '150px' },
      { title: '申请开票金额', dataIndex: 'applyInvoiceTotalFee', customRender: ({ text }) => (text ? formatWithIntl(text) : '---'), align: 'center', width: '120px' },
      { title: '发票金额', dataIndex: 'totalFee', customRender: ({ text }) => (text ? formatWithIntl(text) : '---'), align: 'center', width: '120px' },
      { title: '发票来源', dataIndex: 'invoiceApplyTypeDesc', align: 'center', width: '120px' },
      { title: '发票类型', dataIndex: 'invoiceType', customRender: ({ text }) => invoiceTypeMap[text], align: 'center', width: '120px' },
      { title: '发票状态', dataIndex: 'invoiceStatus', customRender: ({ text }) => invoiceStatusMap[text], align: 'center', width: '120px' },
      { title: '邮寄状态', dataIndex: 'mailingStatus', customRender: ({ text }) => (sendStatusMap[text] || '---'), align: 'center', width: '120px' },
      { title: '发票号', dataIndex: 'oilInvoiceNo', customRender: ({ text }) => (text || '---'), align: 'center', width: '150px' },
      { title: '关联订单', dataIndex: 'rel', slots: { customRender: 'view-order' }, align: 'center', width: '120px' },
      { title: '操作', dataIndex: 'action', width: '200px', slots: { customRender: 'action' }, fixed: 'right' },
    ];

    const tableRef = ref<any>(null);
    // 加载数据方法 必须为 Promise 对象
    const loadTableData = (parameter: any) => new Promise<void>((resolve, reject) => {
      const path = isBefore.value ? '/invoice/find-invoice-list-before' : '/invoice/find-invoice-list-after';

      const params: Record<string, any> = {
        ...parameter, ...not(noEmptyProp(filterParams), ['timeRange']),
      };

      if (filterParams.timeRange.length) {
        params.startTime = moment(filterParams.timeRange[0].format('YYYY-MM-DD 00:00:00')).valueOf();
        params.endTime = moment(filterParams.timeRange[1].format('YYYY-MM-DD 23:59:59')).valueOf();
      }

      request.get(createApiUrl(`/newlinkSass${path}`), { params, noEnterpriseId: true })
        .then((res: any) => {
          resolve({
            ...res.data,
            list: res.data.list.map((item: any) => ({
              ...item, id: `${item.invoiceNo}${item.id}`,
            })),
          });
        }).catch((e: any) => {
          message.error(e.message);
        });
    });

    const onRefreshTable = () => {
      tableRef.value.refresh(true);
    };

    const relOrderModelRef = ref<any>(null as any);
    const onViewRelOrder = (record: any) => {
      console.log('record', record.invoiceRecordId, record);
      if (record.invoiceApplyType === 3) {
        const params: Record<string, any> = { invoiceId: record.invoiceRecordId, invoiceNo: record.invoiceNo };
        if (chargeOrderRef.value) chargeOrderRef.value.show(params);
        return;
      }
      relOrderModelRef.value.view(record);
    };

    const detailDrawerRef = ref<any>(null as any);
    const viewDetail = (id: number) => {
      detailDrawerRef.value.view(id);
    };

    // = ---------------------------- = 发票红冲 = ---------------------------- =
    // 不支持红冲的发票来源（按来源判断，与发票状态无关）：3-充电/占位费订单开发票 5-充电订单服务费开发票
    const RED_FLUSH_UNSUPPORTED_APPLY_TYPES = [3, 5];
    const canApplyRedFlush = (record: any) => !RED_FLUSH_UNSUPPORTED_APPLY_TYPES.includes(Number(record.invoiceApplyType));

    const redFlushApplyRef = ref<any>(null as any);
    const onApplyRedFlush = (record: any) => {
      if (!canApplyRedFlush(record)) return message.warning('该发票来源不支持红冲');
      redFlushApplyRef.value.open(record);
    };

    const redFlushDetailRef = ref<any>(null as any);
    const onViewRedFlushDetail = (record: any) => {
      redFlushDetailRef.value.open(record);
    };

    // = ---------------------------- = 导出数据 = ---------------------------- =
    const confirmVisible = ref(false);
    const exportParams = reactive<{
      timeRange: Moment[];
    }>({
      timeRange: [],
    });

    const onExport = () => {
      Object.assign(exportParams, {
        timeRange: [],
      });
      confirmVisible.value = true;
    };

    let firstSelectTime: Moment | null;
    const isDateDisable = (currentDate: Moment) => {
      if (moment().valueOf() - currentDate.valueOf() < 0) return true;
    };
    const onOpenChange = (value: boolean) => {
      if (!value) firstSelectTime = null;
    };

    const onCalendarChange = (dateRange: [Moment, Moment]) => {
      // eslint-disable-next-line prefer-destructuring
      firstSelectTime = dateRange[0];
    };

    const exporting = ref(false);
    const onConfirmExport = async (event?: Event) => {
      const params: Record<string, any> = {};
      if (!exportParams.timeRange.length) {
        message.warning('请选择时间范围在导出');
        return;
      }
      if (exportParams.timeRange.length) {
        params.startDate = exportParams.timeRange[0].format('YYYY-MM-DD');
        params.endDate = exportParams.timeRange[1].format('YYYY-MM-DD');
      }
      try {
        exporting.value = true;
        await request.get(createApiUrl('/newlinkSass/excel/down/invoiceDetail'), { params: { ...params } });
      } catch (e: Error & any) {
        exporting.value = false;
        return message.error(e.message);
      }
      exporting.value = false;
      notifyExportTaskCreated(event);
      confirmVisible.value = false;
    };

    const getInvoiceApplyType = async () => {
      try {
        const res = await request.get(createApiUrl('/newlinkSass/dic/find-dic-data-list'), { params: { type: 'INVOICE_APPLY_TYPE' } });
        if (res.code !== 200) return;
        sourceList.value = [...sourceList.value, ...res.data];
      } catch (e: Error & any) {
        return message.error(e.message);
      }
    };

    onMounted(() => {
      instance?.$sensors.track('es_saas_tax_list_page_view', { page_name: '发票管理_发票记录页_浏览' });
      getInvoiceApplyType();
    });

    return {
      typeList: [
        { label: '全部', value: '' },
        { label: '电子发票', value: 1 },
        { label: '增值税专用发票', value: 2 },
        // { label: '增值税普通发票', value: 3 },
      ],
      statusList: [
        { label: '全部', value: '' },
        { label: '待开票', value: 0 },
        { label: '开票成功', value: 1 },
        { label: '开票失败', value: 2 },
        { label: '红冲审核中', value: 3 },
        { label: '红冲成功', value: 4 },
        { label: '红冲失败', value: 5 },
      ],
      tableColumns,
      filterParams,

      currentErrorMsg,
      errorModalVisible,
      showError,

      tableRef,
      loadTableData,
      onRefreshTable,

      relOrderModelRef,
      onViewRelOrder,

      detailDrawerRef,
      viewDetail,

      redFlushApplyRef,
      onApplyRedFlush,
      canApplyRedFlush,
      redFlushDetailRef,
      onViewRedFlushDetail,

      isDateDisable,
      onOpenChange,
      onCalendarChange,

      onExport,

      confirmVisible,
      exporting,
      exportParams,
      onConfirmExport,
      sourceList,
      chargeOrderRef,
    };
  },
});
